import { Alert, Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import {
  fetchTiposDiscapacidad,
  selectTiposDiscapacidad,
  selectTiposDiscapacidadEstado,
  selectTiposDiscapacidadError,
} from '../../redux/Utility/UtilitySlice';
import { DiscapacidadEmpleadoDetalle, esOtraDiscapacidad } from './DiscapacidadEmpleadoDetalle';

export const DiscapacidadEmpleadoForm = ({ control, setValue, name = 'empleado', disabled = false }) => {
  const dispatch = useDispatch();
  const catalogoState = useSelector(selectTiposDiscapacidad);
  const estado = useSelector(selectTiposDiscapacidadEstado);
  const errorCatalogo = useSelector(selectTiposDiscapacidadError);
  const campo = (propiedad) => (name ? name + '.' + propiedad : propiedad);
  const tieneHijos = useWatch({ control, name: campo('tieneHijos') });
  const tieneDiscapacidad = useWatch({ control, name: campo('tieneDiscapacidad') });
  const tiposState = useWatch({ control, name: campo('tiposDiscapacidad') });
  const catalogo = (Array.isArray(catalogoState) ? catalogoState : []).filter((tipo) => tipo && typeof tipo === 'object');
  const tipos = (Array.isArray(tiposState) ? tiposState : []).filter((tipo) => tipo && typeof tipo === 'object');

  useEffect(() => {
    if (!disabled) {
      dispatch(fetchTiposDiscapacidad());
    }
  }, [dispatch, disabled]);

  // Los tipos ya asociados se conservan aunque el catálogo deje de ofrecerlos.
  const opciones = [...catalogo, ...tipos.filter((tipo) => !catalogo.some((opcion) => opcion.id === tipo.id))];

  if (disabled) {
    return <DiscapacidadEmpleadoDetalle empleado={{ tieneHijos, tieneDiscapacidad, tiposDiscapacidad: tipos }} />;
  }

  return (
    <Box component="section" sx={{ my: 2 }} aria-label="Información familiar y discapacidad">
      <Typography component="h4" variant="subtitle1" fontWeight="bold" color="black">
        Información familiar y discapacidad
      </Typography>
      <Controller
        name={campo('tieneHijos')}
        control={control}
        defaultValue={null}
        render={({ field }) => (
          <FormControlLabel
            label="Tiene hijos"
            control={<Switch name={field.name} inputRef={field.ref} checked={field.value === true} onBlur={field.onBlur}
              onChange={(_, checked) => field.onChange(checked)} />}
          />
        )}
      />
      <Controller
        name={campo('tieneDiscapacidad')}
        control={control}
        defaultValue={null}
        render={({ field }) => (
          <FormControlLabel
            label="Tiene discapacidad"
            control={<Switch name={field.name} inputRef={field.ref} checked={field.value === true} onBlur={field.onBlur}
              onChange={(_, checked) => {
                field.onChange(checked);
                if (!checked) {
                  setValue(campo('tiposDiscapacidad'), [], { shouldDirty: true, shouldValidate: true });
                }
              }} />}
          />
        )}
      />
      {errorCatalogo && (
        <Alert severity="error" sx={{ my: 1 }} action={
          <Button type="button" color="inherit" onClick={() => dispatch(fetchTiposDiscapacidad())}>Reintentar</Button>
        }>
          {errorCatalogo}
        </Alert>
      )}
      <Controller
        name={campo('tiposDiscapacidad')}
        control={control}
        defaultValue={null}
        rules={{
          validate: (value) => {
            if (tieneDiscapacidad === true && !value?.length) {
              return 'Seleccione al menos un tipo de discapacidad.';
            }
            return !value?.length || tieneDiscapacidad !== false || 'Marque Tiene discapacidad o retire los tipos asociados.';
          },
        }}
        render={({ field, fieldState }) => (
          <div hidden={tieneDiscapacidad !== true && tipos.length === 0}>
            <label className="fw-bold" htmlFor={campo('tiposDiscapacidad')}>Tipos de discapacidad</label>
            <Select
              inputId={campo('tiposDiscapacidad')}
              name={field.name}
              ref={field.ref}
              isMulti
              options={opciones}
              value={tipos}
              getOptionValue={(tipo) => String(tipo?.id ?? '')}
              getOptionLabel={(tipo) => (tipo?.descripcion ?? '') + (tipo?.estado === false ? ' (Inactivo)' : '')}
              isOptionDisabled={(tipo) => tipo.estado === false}
              isLoading={estado === 'loading'}
              isDisabled={estado !== 'succeeded'}
              onBlur={field.onBlur}
              onChange={(seleccion) => field.onChange((seleccion ?? []).map((tipo) =>
                tipos.find((actual) => actual.id === tipo.id) ?? { ...tipo, descripcionEmpleado: '' }
              ))}
              placeholder="Seleccionar tipos de discapacidad"
              noOptionsMessage={() => 'No hay tipos disponibles'}
              loadingMessage={() => 'Cargando tipos de discapacidad...'}
              aria-invalid={!!fieldState.error}
              aria-describedby={fieldState.error ? campo('errorDiscapacidad') : undefined}
              styles={{ menu: (base) => ({ ...base, zIndex: 10 }) }}
            />
            {fieldState.error && <p id={campo('errorDiscapacidad')} className="text-danger">{fieldState.error.message}</p>}
          </div>
        )}
      />
      {tipos.map((tipo, index) => esOtraDiscapacidad(tipo) && (
        <Controller
          key={tipo.id}
          name={campo('tiposDiscapacidad') + '.' + index + '.descripcionEmpleado'}
          control={control}
          defaultValue={tipo.descripcionEmpleado ?? ''}
          rules={{
            validate: (value) => !!value?.trim() || 'Especifique la otra discapacidad.',
            maxLength: { value: 100, message: 'La observación no puede exceder 100 caracteres.' },
          }}
          render={({ field, fieldState }) => (
            <TextField
              name={field.name}
              inputRef={field.ref}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              label={'Observación — ' + tipo.descripcion}
              InputLabelProps={{ sx: { color: 'black', '&.Mui-focused': { color: 'black' } } }}
              fullWidth
              multiline
              minRows={2}
              margin="normal"
              inputProps={{ maxLength: 100 }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? (field.value?.length ?? 0) + '/100'}
            />
          )}
        />
      ))}
    </Box>
  );
};
